import React from 'react';
import { RefreshCw, Clock } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface LastSyncProps {
  lastSyncTime: Date;
  isLoading?: boolean;
  onRefresh?: () => void;
  className?: string;
  showIcon?: boolean;
}

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  }
};

export const LastSync: React.FC<LastSyncProps> = ({
  lastSyncTime,
  isLoading = false,
  onRefresh,
  className,
  showIcon = true
}) => {
  const timeAgo = formatTimeAgo(lastSyncTime);
  const formattedTime = lastSyncTime.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {showIcon && (
        <Clock className="w-4 h-4 text-gray-400" />
      )}
      
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">
          Last sync: {timeAgo}
        </span>
        
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className={cn(
              'p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100',
              'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500',
              isLoading && 'cursor-not-allowed'
            )}
            title={`Last synced at ${formattedTime}. Click to refresh.`}
          >
            <RefreshCw className={cn(
              'w-3 h-3',
              isLoading && 'animate-spin'
            )} />
          </button>
        )}
      </div>
    </div>
  );
};

export default LastSync;